import { gql } from '@apollo/client';

/**
 * Onboarding step fragment
 */
const ONBOARDING_STEP_FIELDS = gql`
  fragment OnboardingStepFields on OnboardingStep {
    id
    title
    description
    category
    order
    isEnabled
    completionCheck
    canSkip
    icon
  }
`;

/**
 * Onboarding config fragment
 */
const ONBOARDING_CONFIG_FIELDS = gql`
  ${ONBOARDING_STEP_FIELDS}
  fragment OnboardingConfigFields on OnboardingConfig {
    id
    name
    description
    isDefault
    isActive
    steps {
      ...OnboardingStepFields
    }
    createdAt
    updatedAt
  }
`;

/**
 * Get the current onboarding status for the tenant
 */
export const GET_ONBOARDING_STATUS = gql`
  ${ONBOARDING_CONFIG_FIELDS}
  query GetOnboardingStatus {
    onboardingStatus {
      status
      currentStep
      completedStepsCount
      totalSteps
      requiredStepsRemaining
      isOnboardingRequired
      completedStepIds
      skippedStepIds
      config {
        ...OnboardingConfigFields
      }
      startedAt
      completedAt
    }
  }
`;

/**
 * Get the active onboarding configuration
 */
export const GET_ONBOARDING_CONFIG = gql`
  ${ONBOARDING_CONFIG_FIELDS}
  query GetOnboardingConfig {
    onboardingConfig {
      ...OnboardingConfigFields
    }
  }
`;

/**
 * Check if onboarding is required (blocking app access)
 */
export const IS_ONBOARDING_REQUIRED = gql`
  query IsOnboardingRequired {
    isOnboardingRequired
  }
`;

/**
 * Start the onboarding process
 */
export const START_ONBOARDING = gql`
  ${ONBOARDING_CONFIG_FIELDS}
  mutation StartOnboarding($input: StartOnboardingInput) {
    startOnboarding(input: $input) {
      status
      currentStep
      completedStepsCount
      totalSteps
      requiredStepsRemaining
      isOnboardingRequired
      completedStepIds
      skippedStepIds
      config {
        ...OnboardingConfigFields
      }
      startedAt
      completedAt
    }
  }
`;

/**
 * Complete an onboarding step
 */
export const COMPLETE_ONBOARDING_STEP = gql`
  mutation CompleteOnboardingStep($input: CompleteOnboardingStepInput!) {
    completeOnboardingStep(input: $input) {
      success
      nextStep
      isOnboardingComplete
      error
    }
  }
`;

/**
 * Skip an onboarding step
 */
export const SKIP_ONBOARDING_STEP = gql`
  mutation SkipOnboardingStep($input: SkipOnboardingStepInput!) {
    skipOnboardingStep(input: $input) {
      success
      nextStep
      isOnboardingComplete
      error
    }
  }
`;

/**
 * Check if a step's auto-completion criteria is met
 */
export const CHECK_STEP_COMPLETION = gql`
  query CheckStepCompletion($stepId: String!) {
    checkStepCompletion(stepId: $stepId)
  }
`;
